import { useState } from "react"
import styled from "styled-components"

import {
  Banner,
  colors,
  Skeleton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { EmptyState } from "src/components/EmptyState"
import { GuestChat } from "src/components/GuestInbox/GuestChat/GuestChat"
import { GuestConversationDetails } from "src/components/GuestInbox/GuestConversations/GuestConversationDetails"
import { GuestConversationDetailsDialog } from "src/components/GuestInbox/GuestConversations/GuestConversationDetailsDialog"
import { GuestConversationHeader } from "src/components/GuestInbox/GuestConversations/GuestConversationHeader"
import { detailsBreakpoint } from "src/components/GuestInbox/GuestConversations/guestConversationStyles"
import { areaPaddingSize } from "src/components/GuestInbox/guestInboxSharedStyles"
import { ScrollArea } from "src/components/ScrollArea/ScrollArea"
import {
  useFetchGuestMessagesInfinite,
  useFetchOrganizationGuest,
  usePostGuestMessage,
} from "src/data/guests/queries/guestQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { Container } from "src/ui/Container/Container"
import Svg404 from "src/ui/icons/404.svg"

export function GuestConversation({
  orgId,
  guestId,
  showSendAsSelector = false,
  showSendMessageSelector = false,
}: {
  orgId: string
  guestId: string
  showSendAsSelector?: boolean
  showSendMessageSelector?: boolean
}) {
  const { tPlaceholder, t, langKeys } = useTranslate()

  const [showDetailsDialog, setShowDetailsDialog] = useState(false)

  const fetchGuest = useFetchOrganizationGuest({
    orgId,
    guestId,
  })

  const fetchConversationMessages = useFetchGuestMessagesInfinite({
    orgId,
    guestId,
    options: {
      refetchInterval: 15000,
    },
  })

  const postGuestMessage = usePostGuestMessage()

  if (fetchGuest.error && fetchGuest.error.status === 404) {
    return <EmptyState title={t(langKeys.guest_not_found)} icon={<Svg404 />} />
  }

  if (fetchGuest.isError || fetchConversationMessages.isError) {
    return (
      <ErrorWrapper>
        <Banner type="error">{t(langKeys.failed_contact_support)}</Banner>
      </ErrorWrapper>
    )
  }

  const flatMessages =
    fetchConversationMessages.data?.pages.flatMap((page) => page.messages) ?? []

  return (
    <StyledContainer>
      <Wrapper>
        <MessagesSection>
          <Header>
            {!fetchGuest.isLoading ? (
              <GuestConversationHeader
                guest={fetchGuest.data}
                onResolve={() => {
                  alert("Not implemented")
                }}
                onDetailsClick={() => setShowDetailsDialog(true)}
              />
            ) : (
              <Skeleton width="30ch" />
            )}
          </Header>

          <ScrollArea>
            <GuestChat
              key={guestId}
              messages={flatMessages}
              onSendMessage={({ message, sendAs, shouldSendMessage }) => {
                postGuestMessage.mutate({
                  path: {
                    organization_id: orgId,
                    guest_id: guestId,
                  },
                  body: {
                    content: message,
                    sender_type: sendAs,
                    send_message: shouldSendMessage,
                  },
                })
              }}
              channel="WhatsApp"
              isSending={postGuestMessage.isPending}
              onLoadMore={() => {
                fetchConversationMessages.fetchPreviousPage()
              }}
              isLoadingMore={fetchConversationMessages.isFetchingPreviousPage}
              showSendAsSelector={showSendAsSelector}
              showSendMessageSelector={showSendMessageSelector}
            />
          </ScrollArea>
        </MessagesSection>

        <DetailsArea>
          <Text variant="heading3" marginBottom={spacing.L}>
            {tPlaceholder("Details")}
          </Text>

          {!fetchGuest.isLoading && (
            <GuestConversationDetails guest={fetchGuest.data} />
          )}
        </DetailsArea>
      </Wrapper>

      {showDetailsDialog && fetchGuest.data && (
        <GuestConversationDetailsDialog
          onClose={() => setShowDetailsDialog(false)}
          guest={fetchGuest.data}
        />
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled(Container)`
  height: 100%;
`

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;
  overflow: hidden;

  @container (${detailsBreakpoint}) {
    > *:not(:last-child) {
      border-right: 1px solid ${colors.divider};
    }

    grid-template-columns: minmax(600px, 2fr) 1fr;
  }
`

const MessagesSection = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`

const Header = styled.div`
  padding: ${areaPaddingSize};
  border-bottom: 1px solid ${colors.divider};
`

const DetailsArea = styled(ScrollArea)`
  display: none;
  padding: ${areaPaddingSize};

  @container (${detailsBreakpoint}) {
    display: block;
  }
`

const ErrorWrapper = styled.div`
  padding: ${areaPaddingSize};
`
